import { Music24Capabilities, music24PlanCapabilities } from 'fym-common/src/license/music24Plans';
import type { RenderLane } from 'fym-shared/src/music24/music24Plans';
import { MUSIC24_GENERAL_PLANS, MUSIC24_PLANS, Music24Plan } from './music24StripePlans';

export type { Music24PlanId } from './music24StripePlans';

/** What each Music24 plan actually grants. The shared table (`fym-common/src/license/music24Plans`)
 * is the only source of truth: music24-api builds the licence answer from it, musicapi and the apps
 * read the same numbers, and the website prints them. Change a limit there, nowhere else. */
export type Music24PlanCapabilities = Music24Capabilities;

/** An account with no valid licence item. Same source the server answers with. */
export const freeCapabilities: Music24PlanCapabilities = music24PlanCapabilities.free;

export function getPlanCapabilities(plan: Music24Plan): Music24PlanCapabilities {
  return music24PlanCapabilities[plan.tier];
}

/** `null` in a limit means unlimited. */
export function isUnlimited(value: number | null): value is null {
  return value === null;
}

/** `null` is unlimited, so it beats every number. */
function moreOf(plan: number | null, current: number | null): boolean {
  if (current === null) {
    return false;
  }
  return plan === null || plan > current;
}

/**
 * The most artists a workspace can have on its plan: the included artists plus the cap of the
 * extra artists. Null is no cap. Every paid plan includes 1 artist, so this cap, not
 * `artistsLimit`, tells which plan gives more artists.
 */
function artistCap(capabilities: Music24PlanCapabilities): number | null {
  if (capabilities.artistsLimit === null || capabilities.extraArtistLimit === null) {
    return null;
  }
  return Math.max(capabilities.artistsLimit, capabilities.includedArtists + capabilities.extraArtistLimit);
}

/** The thing the person just reached for. Callers name it; `generic` is the fallback. */
export enum SMLUpgradeFeature {
  history = 'history',
  browseDepth = 'browseDepth',
  geography = 'geography',
  musicServiceFilter = 'musicServiceFilter',
  insights = 'insights',
  csvExport = 'csvExport',
  followLimit = 'followLimit',
  apiAccess = 'apiAccess',
  pages = 'pages',
  artists = 'artists',
  seats = 'seats',
  socialAccounts = 'socialAccounts',
  scheduledPosts = 'scheduledPosts',
  videoCredits = 'videoCredits',
  aiInsights = 'aiInsights',
  customDomain = 'customDomain',
  fanExport = 'fanExport',
  /** Pre-saves and Pre-save Subscriptions: the fans get every new release (`canUsePresave`). */
  presave = 'presave',
  realtimeAlerts = 'realtimeAlerts',
  artistPortalHistory = 'artistPortalHistory',
  /** The earnings that a distributor reports (Streaming). */
  earnings = 'earnings',
  /** The streams split by store and by country (Streaming). */
  streamingSplit = 'streamingSplit',
  /** The songs in promotion at one time (402 `M24_LIMIT_RELEASES`). */
  releases = 'releases',
  /** Spike and evergreen rollouts for an old song. */
  oldSongs = 'oldSongs',
  /** The lyric video, the visualizer and the music video. */
  fullVideo = 'fullVideo',
  motionArtwork = 'motionArtwork',
  /** The AI clips of the music video editor and the directed video (`canMakeAiFootage` and full-song videos). */
  aiFootage = 'aiFootage',
  /** Palette, logo, font and Instructions of a kit. */
  kitLook = 'kitLook',
  kitZip = 'kitZip',
  /** The place in the render queue. */
  renderLane = 'renderLane',
  /**
   * The Music24 watermark on the files of Free and the trial (trial upsell spec 2026-10-13). A
   * paid plan makes full-size files with no watermark. It replaced `fullQuality`.
   */
  watermark = 'watermark',
  // The AI budget of Free and trial workspaces is spent this month: a paid plan has its own.
  aiPriority = 'aiPriority',
  /** Outreach pitching at all (gate `outreach`): Free finds, a plan with pitches a day pitches. */
  outreach = 'outreach',
  /** The technical cap on the Outreach emails of a month (quota kind `outreachSend`). */
  outreachSends = 'outreachSends',
  /** The own Outreach mailboxes (IMAP and SMTP) of the workspace. */
  outreachMailboxes = 'outreachMailboxes',
  /** Autopilot of an Outreach campaign (gate `outreachAutopilot`, Ultra; Plug keeps it). */
  outreachAutopilot = 'outreachAutopilot',
  /** The CSV export of the Outreach contacts (gate `outreachExport`, Pro and up). */
  outreachExport = 'outreachExport',
  /**
   * More pitches a day ("Pitch 15 a day on Ultra", design outreach-v2 c1, l1): the offer opens with
   * Ultra (`label`) selected (the only plan of `plansForFeature`).
   */
  outreachRate = 'outreachRate',
  /** Connect X, post to it and read its numbers (gate `canUseX`: Pro, Plug, Manager, Label). */
  x = 'x',
  generic = 'generic',
}

const RENDER_LANE_RANK: Record<RenderLane, number> = { last: 0, normal: 1, first: 2 };

/** Does `plan` give more of `feature` than `current` already does? */
export function planUnlocksFeature(
  plan: Music24PlanCapabilities,
  feature: SMLUpgradeFeature,
  current: Music24PlanCapabilities
): boolean {
  switch (feature) {
    case SMLUpgradeFeature.history:
      return plan.historyDaysLimit > current.historyDaysLimit;
    case SMLUpgradeFeature.browseDepth:
      return plan.maxOffset > current.maxOffset;
    case SMLUpgradeFeature.geography:
      return plan.canFilterGeographically && !current.canFilterGeographically;
    case SMLUpgradeFeature.musicServiceFilter:
      return plan.canFilterByMusicService && !current.canFilterByMusicService;
    case SMLUpgradeFeature.insights:
      return plan.canViewListeningInsights && !current.canViewListeningInsights;
    case SMLUpgradeFeature.csvExport:
      return plan.canExport && !current.canExport;
    case SMLUpgradeFeature.followLimit:
      return plan.artistsFollowLimit > current.artistsFollowLimit || plan.tracksFollowLimit > current.tracksFollowLimit;
    case SMLUpgradeFeature.apiAccess:
      return plan.canAccessApi && !current.canAccessApi;
    case SMLUpgradeFeature.pages:
      return moreOf(plan.publishedPagesLimit, current.publishedPagesLimit);
    case SMLUpgradeFeature.artists:
      return moreOf(artistCap(plan), artistCap(current));
    case SMLUpgradeFeature.seats:
      return moreOf(plan.seatsLimit, current.seatsLimit);
    case SMLUpgradeFeature.socialAccounts:
      return moreOf(plan.socialAccountsLimit, current.socialAccountsLimit);
    case SMLUpgradeFeature.scheduledPosts:
      return moreOf(plan.scheduledPostsPerMonth, current.scheduledPostsPerMonth);
    case SMLUpgradeFeature.videoCredits:
      return moreOf(plan.videoCreditsPerMonth, current.videoCreditsPerMonth);
    case SMLUpgradeFeature.aiInsights:
      return moreOf(plan.aiInsightsPerMonth, current.aiInsightsPerMonth);
    case SMLUpgradeFeature.customDomain:
      return plan.canUseCustomDomain && !current.canUseCustomDomain;
    case SMLUpgradeFeature.fanExport:
      return plan.canExportFans && !current.canExportFans;
    case SMLUpgradeFeature.presave:
      return plan.canUsePresave && !current.canUsePresave;
    case SMLUpgradeFeature.realtimeAlerts:
      return plan.realtimeAlerts && !current.realtimeAlerts;
    case SMLUpgradeFeature.artistPortalHistory:
      return moreOf(plan.artistPortalHistoryDays, current.artistPortalHistoryDays);
    case SMLUpgradeFeature.earnings:
      return plan.canSeeEarnings && !current.canSeeEarnings;
    case SMLUpgradeFeature.streamingSplit:
      return plan.canSplitStreaming && !current.canSplitStreaming;
    case SMLUpgradeFeature.releases:
      return moreOf(plan.activeReleasesLimit, current.activeReleasesLimit);
    case SMLUpgradeFeature.oldSongs:
      return plan.canPushOldSongs && !current.canPushOldSongs;
    case SMLUpgradeFeature.fullVideo:
    case SMLUpgradeFeature.motionArtwork:
      return plan.canRenderFullSongVideos && !current.canRenderFullSongVideos;
    case SMLUpgradeFeature.aiFootage:
      // AI clips and directed videos go into a full-song music video, so a plan needs both flags
      // (the "AI clips" row of the plan table; QA 2026-10-28 skp-4, skpr2-1).
      return (
        plan.canMakeAiFootage &&
        plan.canRenderFullSongVideos &&
        !(current.canMakeAiFootage && current.canRenderFullSongVideos)
      );
    case SMLUpgradeFeature.kitLook:
      return plan.canCustomizeKitLook && !current.canCustomizeKitLook;
    case SMLUpgradeFeature.kitZip:
      return plan.canDownloadKitZip && !current.canDownloadKitZip;
    case SMLUpgradeFeature.renderLane:
      return RENDER_LANE_RANK[plan.renderLane] > RENDER_LANE_RANK[current.renderLane];
    case SMLUpgradeFeature.watermark:
      return (current.clipWatermark || current.previewRenders) && !plan.clipWatermark && !plan.previewRenders;
    case SMLUpgradeFeature.aiPriority:
      // Every paid plan uses its own AI budget, so the cheapest paid plan is the offer.
      return true;
    case SMLUpgradeFeature.outreach:
      // Outreach v2: a plan pitches N new people a day (`outreachPitchesPerDay`).
      return plan.outreachPitchesPerDay > current.outreachPitchesPerDay;
    case SMLUpgradeFeature.outreachSends:
      return plan.outreachSendsPerMonth > current.outreachSendsPerMonth;
    case SMLUpgradeFeature.outreachMailboxes:
      return plan.outreachMailboxes > current.outreachMailboxes;
    case SMLUpgradeFeature.outreachAutopilot:
      return plan.canUseOutreachAutopilot && !current.canUseOutreachAutopilot;
    case SMLUpgradeFeature.outreachExport:
      return plan.canExportOutreachContacts && !current.canExportOutreachContacts;
    case SMLUpgradeFeature.outreachRate:
      // Only Label: the plan that the rate card sells (15 a day for each artist, autopilot).
      return plan.plan === 'label' && current.plan !== 'label';
    case SMLUpgradeFeature.x:
      return plan.canUseX && !current.canUseX;
    case SMLUpgradeFeature.generic:
      return true;
    default:
      return false;
  }
}

function defaultPlan(): Music24Plan {
  const plan = MUSIC24_PLANS.find((candidate) => candidate.mostPopular) ?? MUSIC24_PLANS[0];
  if (plan === undefined) {
    throw new Error('MUSIC24_PLANS is empty: the upgrade modal has no plan to offer.');
  }
  return plan;
}

/**
 * The plans to offer for what the person clicked, cheapest first. It is empty when no plan gives
 * more. No client offers the Plug plan (Outreach v2, 2026-10-23): it stays in the plan table for
 * the workspaces on it, but it is never in this list.
 */
export function plansForFeature(feature: SMLUpgradeFeature, current: Music24PlanCapabilities): Music24Plan[] {
  return MUSIC24_GENERAL_PLANS.filter((plan) => planUnlocksFeature(getPlanCapabilities(plan), feature, current));
}

/**
 * The first plan of `plansForFeature`: the cheapest plan that unlocks what the person clicked. A
 * generic trigger, or a feature the top plan already covers, gets the most popular plan.
 */
export function pickPlanForFeature(feature: SMLUpgradeFeature, current: Music24PlanCapabilities): Music24Plan {
  return plansForFeature(feature, current)[0] ?? defaultPlan();
}
